import { cn, ChipsItem, ChipsWrapper, Button } from '@wellesley/ui'
import { SearchIcon } from 'lucide-react'
import { forwardRef } from 'react'

export type Chip = {
  text?: string
  avatarSrc?: string
  show?: boolean
  onClose: () => void
}

type DefaultSearchHeaderInputProps = {
  className?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  readOnly?: boolean
  disabled?: boolean
  chips?: Chip[]
  'data-testid'?: string
  onCancel?: () => void
}

export const DefaultSearchHeaderInput = forwardRef<
  HTMLInputElement,
  DefaultSearchHeaderInputProps
>(
  (
    {
      className,
      placeholder = 'Search...',
      value,
      onChange,
      onFocus,
      onBlur,
      readOnly = false,
      disabled = false,
      chips = [],
      'data-testid': dataTestId = 'search-input',
      onCancel,
    },
    ref,
  ) => {
    const visibleChips = chips.filter((chip) => chip.show)

    return (
      <div
        className={cn(
          'relative flex w-full items-center  md:w-[400px]',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <div className="pointer-events-none absolute left-3 z-10 flex items-center ">
          <SearchIcon
            size={16}
            className="text-gray-500"
            data-testid="search-icon"
          />
        </div>

        <div className="flex w-full items-center gap-xxs rounded-m bg-gray-100 px-xxl py-xxs hover:border-2 hover:border-gray-200 hover:bg-white hover:shadow-[0_0_12px_0_var(--alpha-black-10)]">
          {visibleChips.length > 0 && (
            <ChipsWrapper>
              {visibleChips.map((chip, index) => (
                <ChipsItem
                  key={index}
                  text={chip.text || ''}
                  onRemove={chip.onClose}
                  className="pointer-events-none bg-gray-300 text-gray-900"
                  avatarSrc={chip.avatarSrc}
                  disabled
                />
              ))}
            </ChipsWrapper>
          )}

          {readOnly ? (
            <div className="flex-1 text-left">
              {value ? (
                <span className="text-gray-900">{value}</span>
              ) : (
                <span className="text-gray-500">{placeholder}</span>
              )}
            </div>
          ) : (
            <input
              ref={ref}
              type="text"
              data-testid={dataTestId}
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              disabled={disabled}
              className={cn(
                'flex h-auto  flex-1',
                'border-0 outline-none',
                'focus:outline-none focus:ring-0',
                disabled && 'cursor-not-allowed',
              )}
            />
          )}
        </div>
        {onCancel && (
          <Button
            variant="secondary"
            size="s"
            className="ml-2 md:hidden"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
      </div>
    )
  },
)

DefaultSearchHeaderInput.displayName = 'DefaultSearchHeaderInput'
